import { DecimalPipe } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlertController,
  ModalController,
  Platform,
  PopoverController,
} from "ionic-angular";
import moment from "moment";
import { Observable } from "rxjs/Observable";
import { ISubscription } from "rxjs/Subscription";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { IntercardDataService } from "../intercard-data/intercard-data.service";

const dateTimeFormat = "MM/DD/YYYY HH:mm";

@Component({
  selector: "additional-list-card",
  templateUrl: "additional-list-card.html",
})
export class AdditionalListCardComponent implements OnInit, OnDestroy {
  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;

  id: string;
  name: string;
  componentId: string;
  label: string;
  access: string;
  tooltip: object;
  colHeaders: string[];
  conditionalFormatting: object;
  deltaFields: string[];
  rawData: any[] = [];
  dataRows: any[] = [];
  dummyRow: object;
  prevRow: {};
  declinable: boolean;
  editable: boolean = false;
  manualAdd: boolean = true;
  seeMore: boolean = true;
  seeMoreText: boolean = true;
  seeMoreLength: number = 0;
  moreActions: boolean = false;

  allowExternalData: boolean = false;
  delta: {};

  dataPages: Array<object[]> = [];
  pageSize: number = 5;
  currentPage: number = 0;

  cardDepObs: Observable<any> = null;
  cardDepObsSubscription: ISubscription;
  additionalDetails;

  constructor(
    public modalCtrl: ModalController,
    public dataService: AssessmentDataService,
    public decimalPipe: DecimalPipe,
    public plt: Platform,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    public intercard: IntercardDataService,
    public popover: PopoverController
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.name = this.config.cardName;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined
        ? this.config.label
        : this.config.cardName;
    this.access = this.config.access;
    this.tooltip = this.config.tooltip;
    this.colHeaders = this.config.data.listHeaders;
    this.additionalDetails = this.config.data.additionalDetails;
    this.conditionalFormatting = this.config.data.conditionalFormatting;
    this.deltaFields = this.config.data.deltaFields || [];
    this.declinable = this.config.declinable;
    this.moreActions = this.config.moreActions;
    if (this.config.seeMore) {
      this.seeMore = !this.config.seeMore;
      this.seeMoreLength = this.config.seeMoreLength;
    }

    if (this.config.addNewBtn === true) {
      this.config.onAddNew = this.onAddNew.bind(this);
    }

    if (this.config.editable) {
      this.editable = this.config.editable;
    }
    if (this.config.manualAdd !== undefined && this.config.manualAdd !== null) {
      this.manualAdd = this.config.manualAdd;
    }
    if (this.config.allowExternalData) {
      this.allowExternalData = this.config.allowExternalData;
    }

    this.dummyRow = { id: "" };
    let dummyData = {};
    for (let header of this.colHeaders) {
      dummyData[header] = "-";
    }
    this.dummyRow["values"] = dummyData;

    if (this.allowExternalData) {
      this.cardDepObs = this.intercard.register(this.name);
      this.cardDepObsSubscription = this.cardDepObs.subscribe((data) => {
        if (data === undefined) {
          return;
        } else if (data["declined"] === true || data["noChanges"] === true) {
          let accept = () => {
            let obj = {};
            if (data["declined"]) {
              obj["acceptedDecline"] = true;
            }
            if (data["noChanges"]) {
              obj["acceptedNoChanges"] = true;
            }
            this.intercard.emitData("declines", obj);
          };

          let reject = () => {
            let obj = {};
            if (data["declined"]) {
              obj["rejectedDeclined"] = true;
            }
            if (data["noChanges"]) {
              obj["rejectedNoChanges"] = true;
            }
            this.intercard.emitData("declines", obj);
          };

          this.presentListForm(
            { category: "patientDeclinedAssessment" },
            accept,
            reject
          );
          return;
        }

        let newData = {};
        for (let header of this.colHeaders) {
          if (data[header]) {
            newData[header] = data[header];
          }
        }

        this.addData(newData);
      });
    }
  }

  @Input()
  set data(incomingData: any) {
    if (incomingData) {
      this.dataRows = [];
      if (incomingData.data) {
        this.rawData = incomingData.data;
        for (let data of incomingData.data) {
          let info = {
            id: data.id,
            seemore: this.seeMore,
            seemoretext: this.seeMoreText,
            expanded: false,
          };
          let values = this.parseDataRow(data);
          info["values"] = values;
          this.dataRows.push(info);
        }
      }

      this.updateDataPages();

      if (incomingData.previous) {
        let prevData = incomingData.previous;
        let prevInfo = this.parseDataRow(prevData);
        this.prevRow = prevInfo;
        this.prevRow["id"] = prevData.id;
      }
      this.updateDelta();
    }
  }

  rowHighlighted(col, value) {
    if (this.conditionalFormatting) {
      let formatting = this.conditionalFormatting[col];
      if (formatting) {
        if (
          formatting.type == "greaterThan" &&
          formatting.refValue !== undefined &&
          value > formatting.refValue
        ) {
          return true;
        }
      }
    }

    return false;
  }

  getPageNumber() {
    return this.currentPage + 1;
  }

  presentListForm(data?: object, accept?: Function, reject?: Function): void {
    let newConfig;
    try {
      newConfig = JSON.parse(JSON.stringify(this.config));
    } catch (e) {
      newConfig = this.config;
    }

    let config = { config: newConfig };

    if (this.config.addNewBtn === true) {
      config.config["onAddNew"] = this.onAddNew.bind(this);
    }

    if (data !== undefined) {
      config["data"] = data;
    }

    let listModal = this.modalCtrl.create("InputModalPage", config, {
      enableBackdropDismiss: false,
    });
    listModal.onDidDismiss((data) => {
      if (data !== undefined && data !== null) {
        this.addData(data);
        if (accept) {
          accept();
        }
      } else if (reject) {
        reject();
      }
    });
    listModal.present();
  }

  onAddNew(data: any) {
    this.addData(data);
  }

  updateDelta() {
    if (this.prevRow && this.dataRows[0]) {
      let deltaFields = this.deltaFields;
      let dataRows = this.dataRows;
      let prevRow = this.prevRow;
      this.delta = {};
      for (let header of this.colHeaders) {
        let diff: any = "-";

        if (deltaFields.indexOf(header) == -1) {
          this.delta[header] = diff;
          continue;
        }

        if (header == "date" && moment(dataRows[0].values[header])) {
          let end = moment(dataRows[0].values[header]);
          let start = moment(prevRow[header]);
          let duration = moment.duration(start.diff(end));
          diff = duration.humanize() + " ago";
        } else if (
          !isNaN(dataRows[0].values[header]) &&
          !isNaN(prevRow[header])
        ) {
          diff = dataRows[0].values[header] - prevRow[header];
          diff = parseFloat(this.decimalPipe.transform(diff, "1.0-2"));
          if (diff > 0) {
            diff = "+" + diff;
          }
        }
        this.delta[header] = diff;
      }
    }
  }

  flattenIncomingData(data: object): object {
    let flatData = {};
    for (let key of Object.keys(data)) {
      if (typeof data[key] == "object") {
        let innerObj = data[key];
        if (innerObj == undefined) {
          continue;
        }

        if (key !== "bereavementCharacteristics" && key !== "sendToCareTeam") {
          for (let innerKey of Object.keys(innerObj)) {
            let newKey =
              key + innerKey.replace(/\b\w/g, (l) => l.toUpperCase());
            flatData[newKey] = innerObj[innerKey];
          }
        } else if (key === "bereavementCharacteristics") {
          let arr = data[key].map((item) => this.translatePipe.instant(item));
          flatData[key] = arr.join(", ");
        }

        if (key === "sendToCareTeam") {
          let arr = data[key].map((item) => this.translatePipe.instant(item));
          flatData[key] = arr.join(", ");
        }
      } else {
        flatData[key] = data[key];
      }
    }
    return flatData;
  }

  parseDataRow(data: object) {
    let values = {};
    let flatData = this.flattenIncomingData(data);
    for (let header of this.colHeaders) {
      let val = "-";
      if (flatData[header] || flatData[header] === 0) {
        if (header === "date") {
          flatData[header] = moment(flatData[header]).format(dateTimeFormat);
        } else if (header === "contact") {
          if (flatData[header] == "other") {
            flatData[header] =
              flatData["firstName"] + " " + flatData["lastName"];
          }
        } else if (header == "riskScore") {
          if (flatData[header] > 0 && flatData[header] <= 9) {
            flatData[header] = flatData[header] + " " + "LOW";
          } else if (flatData[header] > 9 && flatData[header] <= 15) {
            flatData[header] = flatData[header] + " " + "MODERATE";
          } else if (flatData[header] > 15 && flatData[header] <= 20) {
            flatData[header] = flatData[header] + " " + "HIGH";
          }
        }
        val = flatData[header];
      }
      values[header] = val;
    }

    //Set additional details
    for (let row of this.additionalDetails[0].rows) {
      for (let header of row.fields) {
        let val = "-";
        if (flatData[header.label] || flatData[header.label] === 0) {
          if (header.concat == false) {
            if (header.label == "sendToCareTeam") {
              val = flatData[header.label].length > 0 ? "true" : "false";
            } else {
              val = flatData[header.label];
            }
          } else {
            let subVal = "";
            for (let subHeader of header.fields) {
              if (flatData[subHeader]) {
                if (
                  subHeader == "address" ||
                  subHeader == "secondaryAddress" ||
                  subHeader == "city"
                ) {
                  subVal += flatData[subHeader] + ", ";
                } else {
                  subVal += flatData[subHeader] + " ";
                }
              }
            }
            val = subVal;
          }
        } else {
          if (header.label == "sendToCareTeam") {
            val = "false";
          } else if (header.label == "address") {
            let subVal = "";
            for (let subHeader of header.fields) {
              if (flatData[subHeader]) {
                if (
                  subHeader == "address" ||
                  subHeader == "secondaryAddress" ||
                  subHeader == "city"
                ) {
                  subVal += flatData[subHeader] + ", ";
                } else {
                  subVal += flatData[subHeader] + " ";
                }
              }
            }
            val = subVal;
          }
        }
        values[header.label] = val;
      }
    }

    return values;
  }

  addData(data: any): void {
    if (data) {
      if (this.assessmentId != null) {
        try {
          data = this.setTextOfDropDown(this.config, "add", data, true);
          this.dataService
            .storeRecord(
              this.assessmentId,
              this.sectionId,
              this.id,
              data,
              this.label
            )
            .subscribe((returnData) => {
              if (!returnData) {
                return;
              }

              let newData = {
                id: returnData.id,
                seemore: this.seeMore,
                seemoretext: this.seeMoreText,
              };
              newData["values"] = this.parseDataRow(returnData);

              this.rawData.unshift(returnData);
              this.dataRows.unshift(newData);
              this.updateDataPages();
              this.updateDelta();
            });
        } catch (error) {}
      }
    }
  }

  editRow(row: object): void {
    let modalConfig: any = {};
    try {
      modalConfig = JSON.parse(JSON.stringify({ config: this.config }));
    } catch (e) {
      modalConfig = { config: this.config };
    }
    if (modalConfig.config.addNewBtn) {
      delete modalConfig.config.addNewBtn;
    }

    let values = this.rawData.filter((record) => record.id == row["id"])[0];
    if (values !== null || values !== undefined) {
      let inputKeys = Object.keys(this.config.inputs);
      let editData = {};

      for (let key of inputKeys) {
        if (values[key] || values[key] == 0) {
          editData[key] = values[key];
        } else {
          editData[key] = null;
        }
      }

      editData = this.setTextOfDropDown(
        modalConfig.config,
        "edit",
        editData,
        false
      );
      modalConfig["data"] = editData;
    }

    let listModal = this.modalCtrl.create("InputModalPage", modalConfig, {
      enableBackdropDismiss: false,
    });
    listModal.onDidDismiss((data) => {
      if (data !== undefined) {
        this.editData(row, row["id"], data);
      }
    });
    listModal.present();
  }

  moreOptions(ev: any, row: any) {
    let config = this.config;
    const popover = this.popover.create(
      "AssessmentMoreOptionsPage",
      { config },
      {
        cssClass:
          "care-teams-options-popover additional-listcard-options-popover",
      }
    );
    popover.onDidDismiss((data) => {
      if (data) {
        if (data === "delete") {
          this.deleteRow(row.id);
        } else if (data === "edit") {
          this.editRow(row);
        }
      }
    });
    popover.present({ ev });
  }
  editData(
    oldRow: object,
    rowId: string,
    data: any,
    caller = "don't care"
  ): void {
    if (data) {
      try {
        data = this.setTextOfDropDown(this.config, "edit", data, true);
        this.dataService
          .editRecord(this.assessmentId, this.sectionId, this.id, rowId, data)
          .subscribe((returnData) => {
            let newData = {
              id: returnData.id,
              seemore: this.seeMore,
              seemoretext: this.seeMoreText,
            };
            newData["values"] = this.parseDataRow(returnData);

            let rawData = this.rawData;
            for (let index in rawData) {
              if (rawData[index]["id"] === returnData.id) {
                rawData[index] = returnData;
                break;
              }
            }

            this.dataRows.splice(this.dataRows.indexOf(oldRow), 1, newData);
            this.updateDataPages();
            this.updateDelta();
          });
      } catch (error) {
        console.log("Error during data submission and building.", error);
      }
    }
  }

  deleteRow(id: string) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("confirmDeleteRecord"),
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            this.dataService
              .deleteRecord(this.assessmentId, this.sectionId, this.id, id)
              .subscribe(() => {
                this.rawData = this.rawData.filter((row) => row.id !== id);
                this.dataRows = this.dataRows.filter((row) => row.id !== id);
                this.updateDataPages();
              });
          },
        },
      ],
    });
    alert.present();
  }

  updateDataPages() {
    this.dataPages = [];

    let dataRows = this.dataRows;
    let pageSize = this.pageSize;
    let dataPages = this.dataPages;
    let pageIndex = -1;
    for (let i = 0; i < dataRows.length; i++) {
      let j = i % pageSize;
      if (j == 0) {
        dataPages.push([]);
        pageIndex++;
      }
      dataPages[pageIndex][j] = dataRows[i];
    }
    if (pageIndex > 0) {
      let endingRow = dataRows.length % pageSize;
      if (endingRow > 0) {
        for (let i = endingRow; i < pageSize; i++) {
          dataPages[pageIndex][i] = this.dummyRow;
        }
      }
    }

    if (this.currentPage > dataRows.length % pageSize) {
      this.prevPage();
    }
  }

  firstPage() {
    if (this.currentPage !== 0) {
      this.currentPage = 0;
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
    }
  }

  setPage(page) {
    if (
      page >= 0 &&
      page <= this.dataPages.length &&
      page !== this.currentPage
    ) {
      this.currentPage = page;
    }
  }

  nextPage() {
    if (this.currentPage < this.dataPages.length - 1) {
      this.currentPage++;
    }
  }

  lastPage() {
    if (this.currentPage !== this.dataPages.length - 1) {
      this.currentPage = this.dataPages.length - 1;
    }
  }

  ngOnDestroy() {
    if (this.cardDepObsSubscription) {
      this.cardDepObsSubscription.unsubscribe();
      this.cardDepObs = null;
    }
  }

  viewMore(row) {
    row.expanded = !row.expanded;
  }

  setTextOfDropDown(config, state, data, save) {
    if (config.inputs) {
      let inputKeys = Object.keys(config.inputs);

      for (let key of inputKeys) {
        let input = config.inputs[key];
        if (input.rules) {
          if (state == "edit" && save == false) {
            if (input.rules.disableOnEdit) {
              input.readonly = true;
            }
          }
          if (data) {
            if (input.rules.storeText) {
              if (input.rules.parentKey) {
                let val = data[key];
                if (state == "edit" && save == false) {
                  val = data[key + "Id"];
                }
                var parentKey = data[input.rules.parentKey];
                var newArray = [];
                if (input.optionGroupsWithIds[parentKey]) {
                  newArray = input.optionGroupsWithIds[parentKey].filter(
                    function (el) {
                      return el.value == val;
                    }
                  );

                  if (newArray.length == 0 && val > 0) {
                    input.optionGroupsWithIds[parentKey].push({
                      display: data["firstName"] + " " + data["lastName"],
                      value: val,
                    });
                    newArray.push({
                      display: data["firstName"] + " " + data["lastName"],
                      value: val,
                    });
                  }
                } else {
                  if (newArray.length == 0 && val > 0) {
                    input.optionGroupsWithIds[parentKey] = [];
                    input.optionGroupsWithIds[parentKey].push({
                      display: data["firstName"] + " " + data["lastName"],
                      value: val,
                    });
                    newArray.push({
                      display: data["firstName"] + " " + data["lastName"],
                      value: val,
                    });
                  }
                }

                if ((state == "add" || state == "edit") && save == true) {
                  if (newArray.length == 0) {
                    data[key] = "other";
                  }
                }
                if ((state == "add" || state == "edit") && save == false) {
                  if (newArray.length == 0) {
                    data[key] = val;
                  }
                }

                for (let i in newArray) {
                  let optionArray = newArray[i];
                  if ((state == "edit" || state == "add") && save == true) {
                    data[key] = optionArray.display;
                  } else if (state == "edit" && save == false) {
                    data[key] = optionArray.value;
                  }
                }
              }
            }
          }

          if (state == "edit" && save == false && input.rules.storeText) {
            if (input.optionGroupsWithDetails) {
              delete input.optionGroupsWithDetails;
            }
          }
        }
      }
    }

    return data;
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/additional-list-card/additional-list-card.ts